<div class="chat-container">
  <div class="chat-header">
    <app-back-button (back)="goBack()"></app-back-button>
    <div class="chat-info">
      <h2>{{ 'Support Chat' | translate }}</h2>
      <p>{{ currentAppealInfo.moduleName }} - {{ currentAppealInfo.exceptionType }}</p>
    </div>
    <div class="chat-actions">
      <!-- Status change buttons - show when status is InProgress (1) -->
      <div *ngIf="showStatusButtons()" class="status-buttons">
        <p-button
          [label]="'Close Ticket' | translate"
          icon="pi pi-check"
          size="small"
          (onClick)="closeTicket()"
          [disabled]="loading"
          styleClass="p-button-outlined status-button">
        </p-button>
        <p-button
          [label]="'Reject Ticket' | translate"
          icon="pi pi-times"
          size="small"
          (onClick)="rejectTicket()"
          [disabled]="loading"
          styleClass="p-button-outlined status-button">
        </p-button>
      </div>
      <p-button
        [label]="'Ticket Details' | translate"
        icon="pi pi-info-circle"
        size="small"
        (onClick)="showAppealDetails()"
        styleClass="p-button-outlined">
      </p-button>
    </div>
  </div>

  <div class="chat-messages" #messagesContainer>
    <app-loading *ngIf="loadingMessages"></app-loading>

    <div *ngFor="let message of messages" class="message" [class.own]="message.isOwn" [class.system]="message.isSystem">
      <div class="message-content">
        <div class="message-header" *ngIf="!message.isSystem">
          <span class="sender-name">{{ message.senderName }}</span>
          <span class="message-time">{{ message.time }}</span>
        </div>
        <div class="message-text">{{ message.text }}</div>
        <div class="message-attachments" *ngIf="message.attachments?.length">
          <a *ngFor="let attachment of message.attachments"
             class="attachment-link"
             [href]="attachment.url"
             [attr.download]="attachment.fileName || attachment.displayName"
             (click)="downloadAttachment(attachment, $event)"
             rel="noopener"
             [title]="attachment.displayName">
            <i class="pi" [ngClass]="getFileIcon(attachment.fileName || attachment.displayName)"></i>
            <span>{{ attachment.displayName }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>

  <div class="chat-input">
    <div class="input-container">
      <textarea
        [(ngModel)]="messageText"
        [placeholder]="'Type your message...' | translate"
        (keydown.enter)="sendMessage()"
        [disabled]="sendingMessage">
      </textarea>

      <div class="input-actions">
        <input
          #fileInput
          type="file"
          multiple
          style="display: none"
          (change)="onFileSelected($event)">

        <p-button
          icon="pi pi-paperclip"
          size="small"
          (onClick)="onAttachmentClick()"
          styleClass="p-button-text">
        </p-button>

        <p-button
          [label]="'Send' | translate"
          icon="pi pi-send"
          (onClick)="sendMessage()"
          [disabled]="!messageText.trim() || sendingMessage"
          [loading]="sendingMessage"
          styleClass="p-button-primary">
        </p-button>
      </div>
    </div>
  </div>

  <!-- File Send Modal -->
  <p-dialog [resizable]="false" [draggable]="false"
    *ngIf="showFileSendModal"
    [header]="'Send Files' | translate"
    [(visible)]="showFileSendModal"
    [modal]="true"
    [style]="{width: '40vw'}"
    [closable]="true"
    (onHide)="onFileSendModalClose()">

    <div class="file-send-content">
      <p>{{ 'Selected Files' | translate }}:</p>
      <div *ngFor="let file of selectedFiles; let i = index" class="file-item">
        <span>{{ file.name }} ({{ getFileSize(file.size) }})</span>
        <p-button
          icon="pi pi-times"
          size="small"
          (onClick)="removeSelectedFile(i)"
          styleClass="p-button-text p-button-danger">
        </p-button>
      </div>

      <div class="file-actions">
        <p-button
          [label]="'Send Files' | translate"
          icon="pi pi-send"
          (onClick)="sendFiles()"
          styleClass="p-button-primary">
        </p-button>
      </div>
    </div>
  </p-dialog>

  <!-- Ticket Details Modal -->
  <p-dialog [resizable]="false" [draggable]="false"
    [header]="'Ticket Details' | translate"
    [(visible)]="showAppealDetailsModal"
    [modal]="true"
    [style]="{width: '50vw'}"
    [closable]="true"
    (onHide)="hideAppealDetails()">

    <div *ngIf="currentAppealInfo.fullTicket" class="ticket-details">
      <div class="detail-row">
        <label>{{ 'Module Name' | translate }}:</label>
        <span>{{ currentAppealInfo.fullTicket.moduleName }}</span>
      </div>

      <div class="detail-row">
        <label>{{ 'Exception Type' | translate }}:</label>
        <span>{{ currentAppealInfo.fullTicket.exceptionType }}</span>
      </div>

      <div class="detail-row">
        <label>{{ 'Device Type' | translate }}:</label>
        <span>{{ currentAppealInfo.fullTicket.userDeviceType }}</span>
      </div>

      <div class="detail-row">
        <label>{{ 'Status' | translate }}:</label>
        <p-tag
          [value]="getStatusLabel(currentAppealInfo.fullTicket?.ticketStatus || 0)"
          [severity]="getStatusSeverity(currentAppealInfo.fullTicket?.ticketStatus || 0)">
        </p-tag>
      </div>

      <div class="detail-row">
        <label>{{ 'Created At' | translate }}:</label>
        <span>{{ currentAppealInfo.fullTicket.createAt | dateFormatLang }}</span>
      </div>

      <div class="detail-row" *ngIf="currentAppealInfo.fullTicket?.exceptionJson">
        <label>{{ 'Exception Details' | translate }}:</label>
        <div class="json-container">
          <p-button
            [label]="showExceptionJson ? 'Hide Details' : 'Show Details'"
            icon="pi pi-eye"
            size="small"
            (onClick)="toggleExceptionJson()"
            styleClass="p-button-text">
          </p-button>

          <div *ngIf="showExceptionJson" class="json-content">
            <pre>{{ formatJson(currentAppealInfo.fullTicket?.exceptionJson) }}</pre>
          </div>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <p-button
        [label]="'Close' | translate"
        icon="pi pi-times"
        (onClick)="hideAppealDetails()"
        styleClass="p-button-text">
      </p-button>
    </ng-template>
  </p-dialog>

  <!-- Close Ticket Confirmation Modal -->
  <app-confirmation-modal
    [(visible)]="showCloseTicketModal"
    [title]="'Close Ticket' | translate"
    [message]="'Are you sure you want to close this ticket?' | translate"
    [confirmLabel]="'Yes' | translate"
    [cancelLabel]="'Cancel' | translate"
    confirmButtonClass="p-button-success"
    (confirm)="onConfirmStatusChange()"
    (cancel)="onCancelStatusChange()">
  </app-confirmation-modal>

  <!-- Reject Ticket Confirmation Modal -->
  <app-confirmation-modal
    [(visible)]="showRejectTicketModal"
    [title]="'Reject Ticket' | translate"
    [message]="'Are you sure you want to reject this ticket?' | translate"
    [confirmLabel]="'Yes' | translate"
    [cancelLabel]="'Cancel' | translate"
    confirmButtonClass="p-button-danger"
    (confirm)="onConfirmStatusChange()"
    (cancel)="onCancelStatusChange()">
  </app-confirmation-modal>
</div>
